/**
 * DeliveryRouteChooser
 *
 * Three delivery options — all write schema into raw HTML seen by every AI crawler.
 *
 * 1. CF Pages / Cloudflare — single _middleware.ts file, one-time deploy, auto-updates.
 * 2. Two snippets           — paste Snippet 1 (static JSON-LD) + Snippet 2 (beacon) once.
 * 3. WordPress plugin       — install once, auto-updates.
 *
 * Persists immediately on select via PATCH /domains/:id/schema-delivery.
 */

import { useState, useEffect } from 'react';
import { api } from '../../../api';
import type { SchemaDeliveryMode } from '../../../types';

interface RouteOption {
  mode: SchemaDeliveryMode;
  icon: string;
  label: string;
  best: string;
  effort: string;
  detail: string;
}

const OPTIONS: RouteOption[] = [
  {
    mode: 'edge_proxy',
    icon: '⚡',
    label: 'Cloudflare Pages',
    best: 'CF Pages sites',
    effort: 'One file, deploy once',
    detail: 'Add one _middleware.ts file to your repo and deploy. Schema auto-updates — never touch it again.',
  },
  {
    mode: 'snippet',
    icon: '📋',
    label: 'Two code snippets',
    best: 'Any platform',
    effort: 'Paste twice, done',
    detail: 'Paste Snippet 1 (inline JSON-LD for AI crawlers) and Snippet 2 (beacon) into your <head>. We notify you when an update is ready.',
  },
  {
    mode: 'wordpress_plugin',
    icon: '🔌',
    label: 'WordPress plugin',
    best: 'WordPress sites',
    effort: 'Install once',
    detail: 'Our plugin writes schema server-side on every page. Set-and-forget — updates deploy automatically.',
  },
];

function resolveInitial(mode: SchemaDeliveryMode): SchemaDeliveryMode {
  return mode === 'beacon_js' ? 'undecided' : mode;
}

function CopyButton({ text, label }: { text: string; label: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={() => {
        void navigator.clipboard.writeText(text).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        });
      }}
      className="text-[11px] font-medium text-indigo-600 hover:text-indigo-800 border border-indigo-200 hover:border-indigo-400 rounded px-2 py-0.5 transition-colors"
    >
      {copied ? '✓ Copied' : label}
    </button>
  );
}

function SnippetBox({ title, content, badge }: { title: string; content: string; badge?: string }) {
  return (
    <div className="mt-3 border border-slate-200 rounded-md overflow-hidden">
      <div className="flex items-center justify-between px-3 py-1.5 bg-slate-50 border-b border-slate-200">
        <span className="text-[11px] font-semibold text-slate-600">{title}</span>
        <div className="flex items-center gap-2">
          {badge && <span className="text-[10px] text-slate-400">{badge}</span>}
          <CopyButton text={content} label="Copy" />
        </div>
      </div>
      <pre className="text-[10px] text-slate-700 p-3 overflow-x-auto leading-relaxed whitespace-pre-wrap break-all">
        {content}
      </pre>
    </div>
  );
}

export function DeliveryRouteChooser({
  domainId,
  initialMode = 'undecided',
}: {
  domainId: string;
  initialMode?: SchemaDeliveryMode;
}) {
  const [selected, setSelected] = useState<SchemaDeliveryMode>(resolveInitial(initialMode));
  const [saving, setSaving] = useState<SchemaDeliveryMode | null>(null);
  const [error, setError] = useState(false);
  const [snippet1, setSnippet1] = useState<string | null>(null);
  const [snippet1Loading, setSnippet1Loading] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [seedError, setSeedError] = useState(false);

  const beaconTag = `<script src="https://api.fixmyseo.io/cdn/tag/${domainId}.js" async></script>`;
  const middlewareUrl = `https://api.fixmyseo.io/cdn/cf-middleware/${domainId}.js`;
  const workerUrl    = `https://api.fixmyseo.io/cdn/cf-worker/${domainId}.js`;
  const pluginUrl = `https://api.fixmyseo.io/cdn/plugins/fixmyseo.zip`;

  useEffect(() => {
    if (selected === 'snippet' && snippet1 === null && !snippet1Loading) {
      setSnippet1Loading(true);
      api.cdn.snippetBlock(domainId)
        .then((text) => setSnippet1(text))
        .catch(() => setSnippet1('<!-- Schema not generated yet — run an audit first -->'))
        .finally(() => setSnippet1Loading(false));
    }
  }, [selected, domainId, snippet1, snippet1Loading]);

  const isPlaceholder = (s: string | null) =>
    !s || s.trim().startsWith('<!-- fixmyseo: no schema');

  const seedSchema = async () => {
    setSeeding(true);
    setSeedError(false);
    try {
      await api.schemaDelivery.seed(domainId);
      // Bypass the 60-second browser cache so we get the freshly-seeded snippet
      const res = await fetch(
        `${(import.meta.env['VITE_API_URL'] ?? (import.meta.env.DEV ? '' : 'https://api.fixmyseo.io'))}/cdn/snippet-block/${domainId}`,
        { cache: 'reload' },
      );
      const text = await res.text();
      setSnippet1(text);
    } catch {
      setSeedError(true);
    } finally {
      setSeeding(false);
    }
  };

  const choose = async (mode: SchemaDeliveryMode) => {
    setSelected(mode);
    setSaving(mode);
    setError(false);
    try {
      await api.schemaDelivery.set(domainId, mode);
    } catch {
      setError(true);
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="bg-white rounded-[6px] p-6 border border-slate-200">
      <p className="text-sm font-semibold text-[#0F172A]">How should we deploy your schema?</p>
      <p className="text-xs text-[#64748B] mt-1 leading-relaxed">
        All three options write schema into raw HTML — visible to every AI engine including
        ChatGPT, Perplexity and Claude. You can change this later.
      </p>

      <div className="grid gap-2.5 mt-4 sm:grid-cols-3">
        {OPTIONS.map((opt) => {
          const isSel = selected === opt.mode;
          return (
            <button
              key={opt.mode}
              onClick={() => void choose(opt.mode)}
              className={`text-left rounded-[6px] border p-3.5 transition-colors ${
                isSel ? 'border-emerald-500 bg-emerald-50/40' : 'border-slate-200 hover:border-slate-300'
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-medium text-[#0F172A]">
                  {opt.icon} {opt.label}
                </span>
                {isSel && (
                  <span className="text-[11px] text-emerald-700 font-semibold shrink-0">
                    {saving === opt.mode ? 'Saving…' : '✓'}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1.5 mt-1.5">
                <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800">
                  AI visible
                </span>
                <span className="text-[10px] text-[#94A3B8]">· {opt.effort}</span>
              </div>
              <p className="text-[11px] text-[#64748B] mt-2 leading-relaxed">{opt.detail}</p>
            </button>
          );
        })}
      </div>

      {error && (
        <p className="text-[11px] text-red-600 mt-2">{"Couldn't save your choice — please try again."}</p>
      )}

      {/* ── Per-mode install instructions ── */}

      {selected === 'edge_proxy' && (
        <div className="mt-4 space-y-3">
          {/* CF Pages Functions variant */}
          <div className="p-3.5 bg-slate-50 rounded-[6px] border border-slate-200">
            <p className="text-[11px] font-semibold text-slate-700 mb-0.5">
              CF Pages Functions
              <span className="ml-1.5 text-[10px] font-normal text-slate-400">(standard Pages, no wrangler.toml)</span>
            </p>
            <ol className="text-[11px] text-slate-600 space-y-1 list-decimal list-inside leading-relaxed mt-1">
              <li>
                <a href={middlewareUrl} download="_middleware.js" className="text-indigo-600 underline font-medium">
                  Download _middleware.js
                </a>
              </li>
              <li>Add to the <code>functions/</code> folder at your repo root</li>
              <li>Push and deploy — done</li>
            </ol>
          </div>
          {/* Workers static-assets variant */}
          <div className="p-3.5 bg-slate-50 rounded-[6px] border border-slate-200">
            <p className="text-[11px] font-semibold text-slate-700 mb-0.5">
              Workers static assets
              <span className="ml-1.5 text-[10px] font-normal text-slate-400">(wrangler.toml with [assets] + main)</span>
            </p>
            <ol className="text-[11px] text-slate-600 space-y-1 list-decimal list-inside leading-relaxed mt-1">
              <li>
                <a href={workerUrl} download="index.js" className="text-indigo-600 underline font-medium">
                  Download index.js
                </a>
              </li>
              <li>Save as your Worker's <code>main</code> entry point (e.g. <code>src/index.js</code>)</li>
              <li>Ensure <code>binding = "ASSETS"</code> under <code>[assets]</code> in wrangler.toml and add your source dir to <code>.assetsignore</code></li>
              <li>Run <code>wrangler deploy</code> — done</li>
            </ol>
          </div>
          <p className="text-[10px] text-slate-400">
            Schema auto-updates from the FixMySEO API — no future deploys required.
          </p>
        </div>
      )}

      {selected === 'snippet' && (
        <div className="mt-4">
          <p className="text-[11px] font-semibold text-slate-700 mb-0.5">
            Paste both into your <code>&lt;head&gt;</code>
          </p>
          <p className="text-[10px] text-slate-500 mb-2">
            Snippet 1 is static — we notify you in the dashboard when an update is available.
            Snippet 2 runs in browsers and never needs updating.
          </p>

          {snippet1Loading ? (
            <div className="mt-3 text-[11px] text-slate-400 py-4 text-center">Loading snippet…</div>
          ) : isPlaceholder(snippet1) ? (
            <div className="mt-3 border border-amber-200 rounded-md bg-amber-50 px-4 py-3 space-y-2">
              <p className="text-[11px] font-semibold text-amber-800">Schema not generated yet</p>
              <p className="text-[11px] text-amber-700">
                Complete your Business Profile so we can generate your schema, then click below.
              </p>
              <button
                onClick={() => void seedSchema()}
                disabled={seeding}
                className="text-[11px] font-medium px-3 py-1 rounded bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50 transition-colors"
              >
                {seeding ? 'Generating…' : 'Generate schema'}
              </button>
              {seedError && (
                <p className="text-[11px] text-red-600">
                  Failed — make sure your Business Profile is complete and try again.
                </p>
              )}
            </div>
          ) : (
            <SnippetBox
              title="Snippet 1 — AI crawler schema (static, paste once)"
              content={snippet1 ?? ''}
              badge="paste into <head>"
            />
          )}

          <SnippetBox
            title="Snippet 2 — Verification beacon (auto-updates, browsers)"
            content={beaconTag}
            badge="paste into <head>"
          />
        </div>
      )}

      {selected === 'wordpress_plugin' && (
        <div className="mt-4 p-3.5 bg-slate-50 rounded-[6px] border border-slate-200">
          <p className="text-[11px] font-semibold text-slate-700 mb-1">One-time install</p>
          <ol className="text-[11px] text-slate-600 space-y-1 list-decimal list-inside leading-relaxed">
            <li>
              <a href={pluginUrl} download className="text-indigo-600 underline font-medium">
                Download fixmyseo.zip
              </a>
            </li>
            <li>Upload via WordPress → Plugins → Add New → Upload</li>
            <li>Activate — schema and updates deploy automatically from here on</li>
          </ol>
        </div>
      )}
    </div>
  );
}
